import { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../lib/supabaseClient';
import { MOCK_DATA } from '../data/mockData';
import { formatPlayerName } from '../lib/nameUtils';

const mockPastMatch = {
  id: 'm1',
  date: 'Hoy, 20:00hs',
  location: 'Cancha Los Amigos',
  type: 'Fútbol 5',
  players: [
    { id: 'p1', name: 'Ezequiel Martínez', position: 'Mediocampista', image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Eze" },
    { id: 'p2', name: 'Damián Rossi', position: 'Arquero', image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Dibu" },
    { id: 'p3', name: 'Lucas Silva', position: 'Delantero', image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Lucas" },
    { id: 'p4', name: 'Juan Pérez', position: 'Defensor', image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Juan" }
  ]
};

const calculateLevel = (xp) => {
  if (xp >= 6000) return 'Leyenda';
  if (xp >= 3000) return 'Avanzado';
  if (xp >= 1500) return 'Intermedio';
  if (xp >= 500) return 'Principiante';
  return 'Novato';
};

export const useCitadosData = ({ view, setNotification, setShowRegister, setView }) => {
  const [profiles, setProfiles] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [activeMatchToRate, setActiveMatchToRate] = useState(mockPastMatch);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [pendingTeamMatch, setPendingTeamMatch] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  
  const [receivedPending, setReceivedPending] = useState([]);
  const [sentPending, setSentPending] = useState([]);
  const [confirmedInvitations, setConfirmedInvitations] = useState([]);
  const [pendingNoShowReports, setPendingNoShowReports] = useState([]);
  const [openSOSAlerts, setOpenSOSAlerts] = useState([]);
  
  const [isConfirmingMatch, setIsConfirmingMatch] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  
  // Admin State
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminStats, setAdminStats] = useState(null);

  const [internalNotifications, setInternalNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hallOfFameSnapshots, setHallOfFameSnapshots] = useState([]);

  // Admin Specific
  const [disputes, setDisputes] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      fetchProfiles(currentUser);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        setShowRegister(false);
      }
      fetchProfiles(currentUser);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  useEffect(() => {
    const checkPendingMatch = async () => {
      if (user && (view === 'landing' || view === 'profile')) {
        try {
          const { data: pendingBookings, error } = await supabase
            .from('citados_player_bookings')
            .select('*, citados_match_reviews(id)')
            .eq('host_id', user.id)
            .in('status', ['accepted', 'agendado'])
            .order('scheduled_at', { ascending: true });

          if (error) throw error;

          if (pendingBookings && pendingBookings.length > 0) {
            const MATCH_DURATION_MS = (1 * 60 + 15) * 60 * 1000;
            const finishedWithoutReview = pendingBookings.filter(b => {
              const matchStartTime = new Date(b.scheduled_at).getTime();
              const now = new Date().getTime();
              const isFinished = now > (matchStartTime + MATCH_DURATION_MS);
              const hasNoReview = !b.citados_match_reviews || b.citados_match_reviews.length === 0;
              return isFinished && hasNoReview;
            });


            if (finishedWithoutReview.length > 0) {
              const timer = setTimeout(() => {
                const matchesToRate = finishedWithoutReview.reduce((acc, curr) => {
                  const key = `${curr.scheduled_at}_${curr.pitch_name}`;
                  if (!acc[key]) {
                    acc[key] = {
                      id: curr.id,
                      date: new Date(curr.scheduled_at).toLocaleDateString(),
                      location: curr.pitch_name || 'Cancha sin nombre',
                      bookingIds: [],
                      players: [],
                      isHost: true
                    };
                  }
                  acc[key].bookingIds.push(curr.id);
                  const p = profiles.find(profile => profile.id === curr.player_id);
                  if (p) {
                    acc[key].players.push({
                      id: p.id,
                      userId: p.userId, // Added userId for notifications
                      bookingId: curr.id,
                      name: p.name,
                      image: p.image || `https://api.dicebear.com/7.x/initials/svg?seed=${p.name}`,
                      position: p.position,
                      gender: p.gender
                    });
                  }
                  return acc;
                }, {});
                
                const firstMatchKey = Object.keys(matchesToRate)[0];
                if (firstMatchKey) {
                  setActiveMatchToRate(matchesToRate[firstMatchKey]);
                  setShowRatingModal(true);
                }
              }, 1500);
              return () => clearTimeout(timer);
            }
          }
        } catch (err) {
          console.error('Error checking pending rating:', err.message);
        }
      }
    };

    checkPendingMatch();
  }, [user, view, profiles]);

  useEffect(() => {
    const checkPendingTeamMatches = async () => {
      if (!user || !profiles.length) return;
      
      const myTeam = profiles.find(p => p.userId === user.id && p.type === 'equipo');
      if (!myTeam) return;

      try {
        const { data, error } = await supabase
          .from('citados_team_matches')
          .select('*')
          .eq('opponent_team_id', myTeam.id)
          .eq('status', 'esperando_confirmacion')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        
        if (data) {
          const reporterTeam = profiles.find(p => p.id === data.reporter_team_id);
          setPendingTeamMatch({
            ...data,
            reporterName: reporterTeam?.name || 'Equipo desconocido',
            reporterImage: reporterTeam?.image
          });
        } else {
          setPendingTeamMatch(null);
        }
      } catch (err) {
        console.error('Error checking pending team matches:', err.message);
      }
    };

    checkPendingTeamMatches();

    if (user && profiles.length) {
      const myTeam = profiles.find(p => p.userId === user.id && p.type === 'equipo');
      if (myTeam) {
        const channel = supabase
          .channel(`team-notifications-${myTeam.id}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'citados_team_matches',
              filter: `opponent_team_id=eq.${myTeam.id}`
            },
            (payload) => {
              const reporterTeam = profiles.find(p => p.id === payload.new.reporter_team_id);
              const reporterName = reporterTeam?.name || 'Un equipo rival';
              
              setPendingTeamMatch({
                ...payload.new,
                reporterName,
                reporterImage: reporterTeam?.image
              });

              setNotification({
                type: 'match_report',
                message: `El equipo ${reporterName} reportó un resultado contra vos: ${payload.new.reporter_goals} - ${payload.new.opponent_goals}.`,
                data: payload.new
              });

              try {
                const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
                audio.volume = 0.3;
                audio.play().catch(() => {});
              } catch (e) {}
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      }
    }
  }, [user, profiles, view]);

  // Bug Fix #20: Notificaciones en tiempo real
  useEffect(() => {
    if (!user) return;

    const notifChannel = supabase
      .channel(`user-notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'citados_notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Si hay cualquier cambio en las notificaciones, refrescamos la lista
          fetchInvitations();
          fetchNotifications();
          
          // Opcional: Sonido de notificación
          try {
            const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
            audio.volume = 0.2;
            audio.play().catch(() => {});
          } catch (e) {}
        }
      )
      .subscribe();

    // Listen to bookings (invitations) directly to update state immediately
    const bookingsChannel = supabase
      .channel(`user-bookings-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'citados_player_bookings'
        },
        (payload) => {
          const myProfileIds = profiles.filter(p => p.userId === user.id).map(p => p.id);
          const isHost = payload.new?.host_id === user.id || payload.old?.host_id === user.id;
          const isPlayer = myProfileIds.includes(payload.new?.player_id) || myProfileIds.includes(payload.old?.player_id);
          
          if (isHost || isPlayer) {
            fetchInvitations();
            
            if (payload.eventType === 'INSERT' || (payload.eventType === 'UPDATE' && payload.new.status !== payload.old?.status)) {
              if (payload.eventType === 'INSERT' && isPlayer) {
                setNotification({ type: 'info', message: '¡Tenés una nueva citación pendiente!' });
                
                // Actualizar campanita local inmediatamente
                const playerNotif = {
                  id: 'temp-p-' + Date.now(),
                  user_id: user.id,
                  title: 'Nueva citación! ⚽',
                  body: `Te han invitado a un partido en ${payload.new.pitch_name || 'Citados'}.`,
                  type: 'invitation',
                  url: '/profile',
                  is_read: false,
                  created_at: new Date().toISOString()
                };
                setInternalNotifications(prev => [playerNotif, ...prev.slice(0, 19)]);
                setUnreadCount(prev => prev + 1);
              } else if (payload.eventType === 'UPDATE' && isHost) {
                let notifTitle = '';
                let notifBody = '';
                let notifType = 'info';

                if (payload.new.status === 'accepted') {
                  setNotification({ type: 'success', message: '¡Aceptaron tu citación! Chat habilitado.' });
                  notifTitle = '¡Invitación aceptada! ⚽';
                  notifBody = `Un jugador aceptó tu citación para ${payload.new.pitch_name || 'el partido'}.`;
                  notifType = 'success';
                } else if (payload.new.status === 'rejected') {
                  setNotification({ type: 'info', message: 'Un jugador rechazó tu citación.' });
                  notifTitle = 'Invitación rechazada ❌';
                  notifBody = `Un jugador no pudo asistir a ${payload.new.pitch_name || 'el partido'}.`;
                  notifType = 'info';
                }

                // Insertar persistencia local (para la campanita)
                if (notifTitle) {
                  const newNotif = {
                    id: 'temp-' + Date.now(),
                    user_id: user.id,
                    title: notifTitle,
                    body: notifBody,
                    type: 'invitation', // Cambiado a invitation para que aparezca el icono de calendario
                    url: '/profile',
                    is_read: false,
                    created_at: new Date().toISOString()
                  };

                  // Actualizar estado local inmediatamente para feedback instantáneo
                  setInternalNotifications(prev => [newNotif, ...prev.slice(0, 19)]);
                  setUnreadCount(prev => prev + 1);

                  console.log('Insertando notificación en DB para host:', user.id);
                  supabase.from('citados_notifications').insert({
                    user_id: user.id,
                    title: notifTitle,
                    body: notifBody,
                    type: 'invitation',
                    url: '/profile'
                  }).then(({ data, error }) => {
                    if (error) {
                      console.error('Error auto-insertando notificación en DB:', error);
                    } else {
                      console.log('Notificación guardada en DB correctamente');
                      fetchNotifications(); // Refrescar con datos reales del server
                    }
                  });
                }
                
                fetchInvitations();
              }
              
              try {
                const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
                audio.volume = 0.2;
                audio.play().catch(() => {});
              } catch (e) {}
            }
          }
        }
      )
      .subscribe();

    // Polling de seguridad cada 2 minutos por si falla el socket
    const pollInterval = setInterval(() => {
      fetchInvitations();
    }, 120000);

    return () => {
      supabase.removeChannel(notifChannel);
      supabase.removeChannel(bookingsChannel);
      clearInterval(pollInterval);
    };
  }, [user, profiles]);

  const fetchInvitations = async () => {
    if (!user) return;
    
    try {
      const myProfileIds = profiles.filter(p => p.userId === user.id).map(p => p.id);
      
      // In dev mode with supabaseAdmin, bypass RLS so we can see data without a real auth session
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;

      // Build query: always filter by host_id, and add player_id filter only if we have profiles
      let query = client
        .from('citados_player_bookings')
        .select('*, citados_match_reviews(id)')
        .order('created_at', { ascending: false });

      if (myProfileIds.length > 0) {
        query = query.or(`host_id.eq.${user.id},player_id.in.(${myProfileIds.join(',')})`);
      } else {
        // User has no profiles yet — only fetch as host
        query = query.eq('host_id', user.id);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (data) {
        const rp = [];
        const sp = [];
        const conf = [];
        const noShows = [];

        data.forEach(invit => {
          if (invit.no_show_status === 'reported' && myProfileIds.includes(invit.player_id)) {
            // Buscamos el perfil del host para mostrar quién reportó
            const host = profiles.find(p => p.id === invit.host_profile_id) || profiles.find(p => p.userId === invit.host_id);
            noShows.push({ 
              ...invit, 
              senderName: host?.name || 'Un Host',
              senderImage: host?.image
            });
          }
          
          const now = new Date();
          const matchTime = new Date(invit.scheduled_at);
          const diffMinutes = (now - matchTime) / (1000 * 60);
          
          // URGENTE: si el partido empieza en menos de 60 mins o ya empezó hace poco
          const isUrgent = diffMinutes >= -60 && diffMinutes <= 20;
          
          // SOBRE LA HORA / LIVE: si ya empezó pero pasaron menos de 20 mins
          const isLiveNow = diffMinutes > 0 && diffMinutes <= 20;

          // Expiración: 10 min para normales, 20 min para urgentes
          const expirationLimit = isUrgent ? 20 : 10;
          const isExpired = diffMinutes > expirationLimit;
          
          if (myProfileIds.includes(invit.player_id)) {
            // Buscamos el perfil del host usando host_profile_id (que es público) en lugar de host_id
            const sender = profiles.find(p => p.id === invit.host_profile_id) || profiles.find(p => p.userId === invit.host_id);
            
            if (invit.status === 'pending') {
              // Gamificación Nivel 1: Semi-anonimato para evitar platform leakage
              const calculatedLevel = sender ? calculateLevel(sender.xp || 0) : 'Desconocido';
              const anonymousName = `Un Host (Nivel ${calculatedLevel})`;
              
              rp.push({ 
                ...invit, 
                senderName: isExpired ? 'Alguien' : anonymousName, 
                senderLevel: calculatedLevel,
                senderXP: sender?.xp || 0,
                // Usamos un avatar de incógnito si está pendiente, o dejamos null para el ícono default
                senderImage: isExpired ? null : null,
                realSenderName: sender?.name, // Guardamos el real por si lo necesitamos después
                isExpired: isExpired,
                isUrgent: isUrgent,
                isLiveNow: isLiveNow
              });
            }
            else if (invit.status === 'accepted' || invit.status === 'finalizado') {
              const hasReview = invit.citados_match_reviews && invit.citados_match_reviews.length > 0;
              conf.push({ ...invit, otherSide: sender, role: 'receiver', hasReview });
            }
          } else {
            const target = profiles.find(p => p.id === invit.player_id);
            
            if (invit.status === 'pending' || invit.status === 'awaiting_payment' || invit.status === 'pending_sos') {
              sp.push({ 
                ...invit, 
                targetName: target?.name || 'Refuerzo SOS 🚨', 
                targetImage: target?.image, 
                isExpired, 
                isUrgent, 
                isLiveNow 
              });
            }
            else if (invit.status === 'accepted' || invit.status === 'finalizado') {
              const hasReview = invit.citados_match_reviews && invit.citados_match_reviews.length > 0;
              conf.push({ ...invit, otherSide: target, role: 'host', hasReview });
            }
          }
        });

        // ENRIQUECIMIENTO DE USER_ID PARA CHATS DE CONFIRMED BOOKINGS (HOST SIDE)
        const bookingsAsHost = conf.filter(c => c.role === 'host');
        if (bookingsAsHost.length > 0) {
          const playerProfileIds = [...new Set(bookingsAsHost.map(c => c.player_id))];
          
          // Verificar si alguno de estos perfiles de jugadores no tiene su userId asignado en la lista de perfiles
          const needsUpdate = playerProfileIds.some(pid => {
            const p = profiles.find(profile => profile.id === pid);
            return p && !p.userId;
          });

          if (needsUpdate) {
            const { data: playerProfiles, error: profileErr } = await client
              .from('citados_profiles')
              .select('id, user_id')
              .in('id', playerProfileIds);
              
            if (!profileErr && playerProfiles) {
              const userIdMap = {};
              playerProfiles.forEach(p => {
                userIdMap[p.id] = p.user_id;
              });
              
              // Enriquecer otherSide en las invitaciones locales de conf
              conf.forEach(c => {
                if (c.role === 'host' && c.otherSide) {
                  c.otherSide.userId = userIdMap[c.player_id];
                }
              });

              // Enriquecer el estado global de profiles para que MatchChat y la pantalla de Calificaciones lo usen
              setProfiles(prevProfiles => 
                prevProfiles.map(p => {
                  if (userIdMap[p.id] && !p.userId) {
                    return { ...p, userId: userIdMap[p.id] };
                  }
                  return p;
                })
              );
            }
          } else {
            // Si ya tienen el userId cargado en el estado de profiles, simplemente lo asignamos a conf
            conf.forEach(c => {
              if (c.role === 'host' && c.otherSide) {
                const p = profiles.find(profile => profile.id === c.player_id);
                if (p && p.userId) {
                  c.otherSide.userId = p.userId;
                }
              }
            });
          }
        }

        setReceivedPending(rp);
        setSentPending(sp);
        setConfirmedInvitations(conf);
        setPendingNoShowReports(noShows);
      }

      // Fetch open SOS bookings from other hosts
      let sosQuery = client
        .from('citados_player_bookings')
        .select('*')
        .eq('is_sos', true)
        .eq('status', 'pending_sos')
        .is('player_id', null);

      if (user) {
        sosQuery = sosQuery.neq('host_id', user.id);
      }

      const { data: sosData, error: sosError } = await sosQuery;
      if (!sosError && sosData) {
        const enrichedSOS = sosData.map(sos => {
          const host = profiles.find(p => p.id === sos.host_profile_id) || profiles.find(p => p.userId === sos.host_id);
          return {
            ...sos,
            hostName: host?.name || 'Organizador',
            hostImage: host?.image || null,
            hostReputation: host?.reputation || 100,
            hostAttendanceRate: host?.attendanceRate || 100,
            hostLevel: host?.level || 'Novato',
            hostXp: host?.xp || 0
          };
        });
        setOpenSOSAlerts(enrichedSOS);
      }
    } catch (err) {
      console.error('Error fetching invitations:', err.message);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [user, profiles]);

  const handleAcceptInvitation = async (invit) => {
    try {
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;

      // 1. Validar conflictos de horario y límites diarios (Salud Citados)
      const { data: userProfiles } = await client
        .from('citados_profiles')
        .select('id')
        .eq('user_id', user.id);
      const profileIds = userProfiles?.map(p => p.id) || [];

      const matchDate = new Date(invit.scheduled_at);
      const startOfDay = new Date(matchDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(matchDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Buscamos partidos donde el usuario sea jugador (cualquier perfil) o host, y estén confirmados
      const filterStr = profileIds.length > 0 
        ? `player_id.in.(${profileIds.join(',')}),host_id.eq.${user.id}`
        : `host_id.eq.${user.id}`;

      const { data: existingMatches, error: fetchError } = await client
        .from('citados_player_bookings')
        .select('scheduled_at, status')
        .or(filterStr)
        .in('status', ['accepted', 'agendado'])
        .gte('scheduled_at', startOfDay.toISOString())
        .lte('scheduled_at', endOfDay.toISOString());

      if (fetchError) throw fetchError;

      if (existingMatches && existingMatches.length > 0) {
        // Límite de 2 partidos por día
        if (existingMatches.length >= 2) {
          setNotification({
            type: 'error',
            message: 'Ya tenés 2 partidos para este día. ¡Cuidá tu salud! 🧘‍♂️'
          });
          return;
        }

        // Brecha de salud: al menos 2 horas entre inicios
        const newMatchTime = matchDate.getTime();
        const MIN_GAP_MS = 2 * 60 * 60 * 1000; // 2 horas de diferencia mínima

        for (const match of existingMatches) {
          const existingTime = new Date(match.scheduled_at).getTime();
          const diff = Math.abs(newMatchTime - existingTime);
          
          if (diff < MIN_GAP_MS) {
            setNotification({
              type: 'error',
              message: 'Este partido está muy cerca de otro que ya tenés. Dejá pasar al menos 2 horas entre inicios. ⚡'
            });
            return;
          }
        }
      }

      // NUEVO: Validación de "Cupos" (Evitar sobreventa si el host invitó a más personas de las que necesitaba)
      const spotsNeeded = invit.spots_needed || 1; // Fallback a 1 si es null/viejo
      const { data: hostAcceptedMatches } = await client
        .from('citados_player_bookings')
        .select('id')
        .eq('host_id', invit.host_id)
        .eq('scheduled_at', invit.scheduled_at)
        .eq('status', 'accepted');
        
      // Filtramos por pitch_name en el frontend por si algún pitch_name es null y causa problemas en el .eq
      const sameMatchAccepted = (hostAcceptedMatches || []).filter(m => true); // Asumimos que mismo host y hora = mismo partido
      
      if (sameMatchAccepted.length >= spotsNeeded) {
        setNotification({
          type: 'error',
          message: 'Lo sentimos, los cupos para este partido ya fueron ocupados.'
        });
        
        // Auto-rechazamos para que ya no lo vea como pendiente
        await client.from('citados_player_bookings').update({ status: 'rejected' }).eq('id', invit.id);
        
        // Solo reintegrar crédito si la invitación fue paga (usó crédito o pagó con MP)
        const wasCharged = invit.used_credit === true || (invit.fee_paid && invit.fee_paid > 0);
        if (invit.status === 'pending' && invit.host_profile_id && wasCharged) {
          await client.rpc('add_credit', { p_profile_id: invit.host_profile_id });
          
          // Notificar al host que se le devolvió un crédito porque los cupos se llenaron
          try {
            await client.from('citados_notifications').insert({
              user_id: invit.host_id,
              title: 'Cupos llenos 🔄',
              body: `Alguien más ya ocupó el lugar en tu partido en ${invit.pitch_name}. Te hemos devuelto 1 crédito.`,
              type: 'info',
              url: '/profile'
            });
          } catch (err) {}
        }

        fetchInvitations();
        return;
      }

      // 2. Si pasa las validaciones, procedemos a aceptar
      const { error } = await client
        .from('citados_player_bookings')
        .update({ status: 'accepted' })
        .eq('id', invit.id)
        .eq('player_id', invit.player_id);

      if (error) throw error;

      setNotification({
        type: 'success',
        message: '¡Invitación aceptada! Chat habilitado para coordinar.'
      });

      // Insert notification for the host
      try {
        const myProfile = profiles.find(p => p.id === invit.player_id);
        const { error: notifErr } = await client.from('citados_notifications').insert({
          user_id: invit.host_id,
          title: '¡Invitación aceptada! ⚽',
          body: `${myProfile?.name || 'Un jugador'} aceptó tu citación para el partido en ${invit.pitch_name}.`,
          type: 'success',
          url: '/profile'
        });
        
        if (notifErr) {
          console.error('Error insertando notificación (aceptación) en DB:', notifErr);
          // Fallback log para debugging local
          console.log('Intento de inserción para user_id:', invit.host_id);
        } else {
          console.log('Notificación de aceptación enviada al host correctamente');
        }
      } catch (err) {
        console.error('Excepción al enviar notificación al host:', err);
      }

      fetchInvitations();
    } catch (err) {
      console.error('Error al aceptar invitación:', err);
      setNotification({ type: 'error', message: 'Error al aceptar invitación' });
    }
  };

  const handleRejectInvitation = async (invit) => {
    try {
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;
      const { error } = await client
        .from('citados_player_bookings')
        .update({ status: 'rejected' })
        .eq('id', invit.id)
        .eq('player_id', invit.player_id);

      if (error) throw error;

      // Solo reintegrar crédito si la invitación fue paga (usó crédito o pagó con MP)
      const wasCharged = invit.used_credit === true || (invit.fee_paid && invit.fee_paid > 0);
      if (invit.status === 'pending' && invit.host_profile_id && wasCharged) {
        await client.rpc('add_credit', { p_profile_id: invit.host_profile_id });
      }

      setNotification({
        type: 'success',
        message: wasCharged
          ? 'Invitación rechazada. Se devolvió 1 crédito al organizador.'
          : 'Invitación rechazada.'
      });

      // Notificar al host del rechazo
      try {
        const myProfile = profiles.find(p => p.id === invit.player_id);
        const { error: notifErr } = await client.from('citados_notifications').insert({
          user_id: invit.host_id,
          title: 'Invitación rechazada ❌',
          body: `${myProfile?.name || 'Un jugador'} no puede asistir al partido en ${invit.pitch_name}.`,
          type: 'info',
          url: '/profile'
        });

        if (notifErr) {
          console.error('Error insertando notificación (rechazo) en DB:', notifErr);
          console.log('Intento de inserción para user_id:', invit.host_id);
        } else {
          console.log('Notificación de rechazo enviada al host correctamente');
        }
      } catch (err) {
        console.error('Excepción al enviar notificación de rechazo al host:', err);
      }

      fetchInvitations();
    } catch (err) {
      setNotification({ type: 'error', message: 'Error al rechazar invitación' });
    }
  };

  useEffect(() => {
    const fetchRecentMatches = async () => {
      if (!user || view !== 'profile') return;

      try {
        const myProfileIds = profiles.filter(p => p.userId === user.id).map(p => p.id);

        // Fetch Team Matches (where user is captain or participant)
        // Por ahora lo dejamos global o filtrado por captain_id si aplica
        const { data: teamData, error: teamError } = await supabase
          .from('citados_team_matches')
          .select('*')
          .eq('status', 'finalizado')
          .order('created_at', { ascending: false })
          .limit(5);

        if (teamError) throw teamError;

        // Fetch Bookings (Only user's bookings: as host or as player)
        let bookingQuery = supabase
          .from('citados_player_bookings')
          .select('*')
          .eq('status', 'finalizado');

        if (myProfileIds.length > 0) {
          bookingQuery = bookingQuery.or(`host_id.eq.${user.id},player_id.in.(${myProfileIds.join(',')})`);
        } else {
          bookingQuery = bookingQuery.eq('host_id', user.id);
        }

        const { data: bookingData, error: bookingError } = await bookingQuery
          .order('scheduled_at', { ascending: false })
          .limit(10);

        if (bookingError) throw bookingError;

        const combined = [];

        // Enrich Team Matches
        if (teamData) {
          teamData.forEach(match => {
            const reporter = profiles.find(p => p.id === match.reporter_team_id);
            const opponent = profiles.find(p => p.id === match.opponent_team_id);
            combined.push({
              ...match,
              date: new Date(match.created_at),
              reporterName: reporter?.name || 'Equipo mánager',
              reporterImage: reporter?.image,
              opponentName: opponent?.name || 'Equipo rival',
              opponentImage: opponent?.image,
              location: 'Partido de Equipo',
              match_type: 'Fútbol'
            });
          });
        }

        // Enrich Bookings (Citations)
        if (bookingData) {
          bookingData.forEach(booking => {
            const host = profiles.find(p => p.id === booking.host_profile_id);
            const player = profiles.find(p => p.id === booking.player_id);
            combined.push({
              id: booking.id,
              date: new Date(booking.scheduled_at),
              created_at: booking.scheduled_at, // Fix "Invalid Date"
              reporterName: host?.name || 'Organizador',
              reporterImage: host?.image,
              opponentName: player?.name || 'Invitado',
              opponentImage: player?.image,
              reporter_goals: null,
              opponent_goals: null,
              location: booking.pitch_name || 'Cancha por confirmar',
              match_type: booking.match_type_label || 'Fútbol',
              isCitation: true
            });
          });
        }

        // Sort by date descending
        combined.sort((a, b) => b.date - a.date);
        
        setRecentMatches(combined.slice(0, 5));
      } catch (err) {
        console.error('Error fetching recent matches:', err.message);
      }
    };

    fetchRecentMatches();
  }, [user, profiles, view]);

  const handleConfirmTeamMatch = async (match) => {
    if (isConfirmingMatch || match.status === 'finalizado') return;
    setIsConfirmingMatch(true);
    try {
      const { error: updateError } = await supabase
        .from('citados_team_matches')
        .update({ status: 'finalizado', validated_at: new Date().toISOString() })
        .eq('id', match.id)
        .eq('status', 'esperando_confirmacion');

      if (updateError) throw updateError;

      const reporterWon = match.reporter_goals > match.opponent_goals;
      const opponentWon = match.opponent_goals > match.reporter_goals;

      const { data: reporterProfile } = await supabase
        .from('citados_profiles')
        .select('team_matches_played, team_wins, team_goals_scored, team_goals_conceded')
        .eq('id', match.reporter_team_id)
        .maybeSingle();

      if (reporterProfile) {
        await supabase.from('citados_profiles').update({
          team_matches_played: (reporterProfile.team_matches_played || 0) + 1,
          team_wins: (reporterProfile.team_wins || 0) + (reporterWon ? 1 : 0),
          team_goals_scored: (reporterProfile.team_goals_scored || 0) + match.reporter_goals,
          team_goals_conceded: (reporterProfile.team_goals_conceded || 0) + match.opponent_goals,
        }).eq('id', match.reporter_team_id);
      }

      const { data: opponentProfile } = await supabase
        .from('citados_profiles')
        .select('team_matches_played, team_wins, team_goals_scored, team_goals_conceded')
        .eq('id', match.opponent_team_id)
        .maybeSingle();

      if (opponentProfile) {
        await supabase.from('citados_profiles').update({
          team_matches_played: (opponentProfile.team_matches_played || 0) + 1,
          team_wins: (opponentProfile.team_wins || 0) + (opponentWon ? 1 : 0),
          team_goals_scored: (opponentProfile.team_goals_scored || 0) + match.opponent_goals,
          team_goals_conceded: (opponentProfile.team_goals_conceded || 0) + match.reporter_goals,
        }).eq('id', match.opponent_team_id);
      }

      setPendingTeamMatch(null);
      setNotification({ type: 'success', message: '¡Partido confirmado! Las estadísticas se actualizaron.' });
      fetchProfiles();
    } catch (err) {
      console.error('Error confirming team match:', err);
      setNotification({ type: 'error', message: 'Error al confirmar el partido.' });
    } finally {
      setIsConfirmingMatch(false);
    }
  };

  const handleDisputeTeamMatch = async (match) => {
    try {
      const { error } = await supabase
        .from('citados_team_matches')
        .update({ status: 'disputado' })
        .eq('id', match.id);

      if (error) throw error;

      setPendingTeamMatch(null);
      setNotification({ type: 'error', message: 'Partido marcado como disputado. No se sumaron stats.' });
    } catch (err) {
      console.error('Error disputing team match:', err);
      setNotification({ type: 'error', message: 'Error al disputar el partido.' });
    }
  };

  const handleDisputeNoShow = async (booking) => {
    try {
      const { error } = await supabase
        .from('citados_player_bookings')
        .update({ no_show_status: 'disputed', no_show: false })
        .eq('id', booking.id);
      
      if (error) throw error;
      
      setPendingNoShowReports(prev => prev.filter(b => b.id !== booking.id));
      setNotification({ show: true, message: 'Falta disputada. Un admin revisará el caso.', type: 'success' });
    } catch (err) {
      console.error('Error al disputar:', err);
    }
  };

  const handleConfirmNoShow = async (booking) => {
    try {
      // 1. Llamada Atómica al RPC para confirmar la falta y aplicar penalizaciones
      const { data: rpcResult, error: rpcError } = await supabase.rpc('confirm_no_show_penalty', {
        p_booking_id: booking.id
      });
      
      if (rpcError) throw rpcError;
      if (!rpcResult?.success) throw new Error(rpcResult?.error || "Error al confirmar falta");

      // 2. Limpiar estado local
      setPendingNoShowReports(prev => prev.filter(b => b.id !== booking.id));
      
      // 3. Mensaje educativo sobre Fair Play
      setNotification({ 
        type: 'penalty', 
        message: '⚠️ PENALIZACIÓN APLICADA: Tu inasistencia afecta tu Reputación y XP. El Fair Play es sagrado en Citados para mantener la calidad de los partidos.'
      });

      fetchProfiles();
      fetchInvitations();
    } catch (err) {
      console.error('Error al confirmar falta:', err);
      setNotification({ type: 'error', message: 'Error al procesar la confirmación: ' + err.message });
    }
  };

  const handleSaveRating = async (ratingData) => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      // 1. Obtener el perfil del reviewer
      const myProfile = profiles.find(p => p.userId === user.id);
      if (!myProfile) throw new Error("No se encontró el perfil del usuario");

      // 2. Llamada Atómica al RPC de Supabase (Maneja XP, Stats, Reputación y Créditos)
      const { data: rpcResult, error: rpcError } = await supabase.rpc('submit_match_report_v4', {
        p_match_id: ratingData.matchId,
        p_mvp_id: ratingData.mvpId || null,
        p_rating: ratingData.starRating,
        p_comment: ratingData.comment || '',
        p_absent_player_ids: ratingData.absentIds || []
      });

      if (rpcError) throw rpcError;
      if (!rpcResult?.success) throw new Error(rpcResult?.error || "Error en el servidor");

      // 3. Notificar a los jugadores involucrados
      try {
        const hostName = myProfile.name || 'El organizador';
        const location = activeMatchToRate.location || 'el partido';
        const notificationsToInsert = [];

        // Notificaciones para jugadores presentes
        activeMatchToRate.players
          .filter(p => !ratingData.absentIds.includes(p.id))
          .forEach(player => {
            const isMvp = player.id === ratingData.mvpId;
            
            if (activeMatchToRate.isHost) {
              notificationsToInsert.push({
                user_id: player.userId,
                title: isMvp ? '¡Fuiste el MVP! ⭐' : 'Partido finalizado ✅',
                body: isMvp 
                  ? `${hostName} te eligió como el mejor del partido en ${location}.`
                  : `${hostName} confirmó tu asistencia al partido en ${location}.`,
                type: 'rating',
                url: '/dashboard'
              });
            } else if (player.id !== myProfile.id) {
              // Si soy Guest, notifico al Host que lo califiqué (pero evito auto-notificarme)
              notificationsToInsert.push({
                user_id: player.userId,
                title: '¡Te calificaron! ⭐',
                body: `${myProfile.name} te calificó por el partido en ${location}.`,
                type: 'rating',
                url: '/dashboard'
              });
            }
          });

        // Notificaciones para inasistencias (Solo si soy Host)
        if (activeMatchToRate.isHost && ratingData.absentIds.length > 0) {
          const absentPlayers = activeMatchToRate.players.filter(p => ratingData.absentIds.includes(p.id));
          for (const player of absentPlayers) {
            notificationsToInsert.push({
              user_id: player.userId,
              title: '⚠️ Reporte de Inasistencia',
              body: `El organizador reportó que no asististe al partido en ${location}. Tenés 24hs para disputarlo o se aplicará la penalización automáticamente.`,
              type: 'warning',
              url: '/dashboard'
            });
          }
        }

        if (notificationsToInsert.length > 0) {
          await supabase.from('citados_notifications').insert(notificationsToInsert);
        }
      } catch (notifErr) {
        console.warn('Error al procesar notificaciones:', notifErr.message);
      }

      setNotification({
        type: 'success',
        message: activeMatchToRate.isHost 
          ? '¡Reporte finalizado! Sumaste +150 XP por organizar.' 
          : '¡Valoración enviada! Sumaste +50 XP.'
      });
      
      // 4. Refrescar datos locales
      fetchProfiles();
      fetchInvitations(); // Para actualizar hasReview y ocultar botón de reporte
      
    } catch (error) {
      console.error('Error en el flujo de calificación:', error.message);
      setNotification({
        type: 'error',
        message: 'No se pudo guardar la calificación: ' + error.message
      });
    } finally {
      setIsSaving(false);
    }
  };

  const mapProfile = (p) => {
    const rawAvailability = Array.isArray(p.availability) ? p.availability : (p.specialty?.includes('F5') ? [5, 7] : [7, 11]);
    const sortedAvailability = [...new Set(rawAvailability.map(Number))]
      .filter(n => !isNaN(n))
      .sort((a, b) => a - b);

    // Parse and sort the string specialty if raw database specialty contains numbers (e.g. "F11 F5 F7")
    let specialtyStr = p.specialty;
    if (p.specialty && typeof p.specialty === 'string') {
      const matches = p.specialty.match(/\d+/g);
      if (matches) {
        const sortedNums = [...new Set(matches.map(Number))].sort((a, b) => a - b);
        specialtyStr = sortedNums.map(n => `F${n}`).join(' ');
      }
    }

    return {
      id: p.id,
      userId: p.user_id,
      type: p.type,
      name: formatPlayerName(p.name, p.show_full_name, false, p.type),
      fullName: p.name,
      show_full_name: p.show_full_name,
      city: p.city,
      area: p.area,
      level: p.level,
      ageRange: p.age_range,
      isAvailable: p.is_available,
      availableFrom: p.available_from,
      credits: p.credits || 0,
      availableTo: p.available_to,
      isMixedAvailable: p.is_mixed_available,
      gender: p.gender,
      matchesPlayed: p.matches_played || 0,
      matchesTotal: p.matches_total || 0,
      mvpsCount: p.mvps_count || 0,
      xp: p.xp || 0,
      stats: {
        matches: p.matches_played || 0,
        matchesTotal: p.matches_total || 0,
        mvpAwards: p.mvps_count || 0,
        goalsScored: p.goals_count || 0,
        assists: p.assists_count || 0,
        cleanSheets: p.clean_sheets_count || 0,
        attendance: p.matches_total > 0 ? (p.matches_played / p.matches_total) : 1, 
        xp: p.xp || 0
      },
      teamCategory: p.category,
      team_matches_played: p.team_matches_played || 0,
      team_wins: p.team_wins || 0,
      team_goals_scored: p.team_goals_scored || 0,
      team_goals_conceded: p.team_goals_conceded || 0,
      team_reputation: p.team_reputation || 100,
      positions: Array.isArray(p.positions) ? p.positions : (p.position ? [p.position] : []),
      availability: sortedAvailability,
      position: p.type === 'jugador' 
        ? ((Array.isArray(p.positions) && p.positions.length > 0) ? p.positions.join(', ') : (p.position || 'Sin puesto')) 
        : (p.category || p.position || 'Busca Rival'),
      location: p.city || p.location || 'General Roca', 
      rating: p.rating || 5.0,
      reputation: p.reputation || p.attendanceRate || 100,
      reviews: p.matches_played || p.reviews || 0,
      matchesPlayed: p.matches_played || p.reviews || 0,
      matchesTotal: p.matches_total || (p.matches_played || p.reviews || 0),
      attendanceRate: p.matches_total > 0 ? Math.round((p.matches_played / p.matches_total) * 100) : (p.attendanceRate || 100),
      price: p.type === 'jugador' ? 3500 : 0,
      image: p.image_url || p.image || "https://api.dicebear.com/7.x/avataaars/svg?seed=Citados",
      specialty: p.type === 'jugador' 
        ? (sortedAvailability.length > 0 ? sortedAvailability.map(v => `F${v}`).join(' ') : (specialtyStr || p.level || 'Nuevo')) 
        : (sortedAvailability.length > 0 ? sortedAvailability.map(v => `F${v}`).join(' ') : (specialtyStr || 'F5 F7 F11'))
    };
  };

  const searchByRadius = async (lat, lng, radiusKm) => {
    try {
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;
      const { data, error } = await client.rpc('search_profiles_by_radius', {
        target_lat: lat,
        target_lng: lng,
        radius_meters: radiusKm * 1000
      });
      
      if (error) {
        console.error("Error from RPC search_profiles_by_radius, falling back to JS filter.", error.message);
        // Fallback to JS filtering if RPC is not deployed or fails
        const radiusMeters = radiusKm * 1000;
        const R = 6371e3; // Earth radius in meters
        const rad = (deg) => deg * Math.PI / 180;
        const fallbackResults = profiles.filter(p => {
          if (!p.latitude || !p.longitude) return false;
          const dLat = rad(p.latitude - lat);
          const dLon = rad(p.longitude - lng);
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(rad(lat)) * Math.cos(rad(p.latitude)) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          return R * c <= radiusMeters;
        });
        return fallbackResults;
      }
      
      if (data) {
        return data.map(mapProfile);
      }
      return [];
    } catch (err) {
      console.error('Error fetching radius profiles:', err);
      return [];
    }
  };

  const fetchProfiles = async (currentUser = user) => {
    setIsLoading(true);
    try {
      const [publicRes, snapshotsRes] = await Promise.all([
        supabase.from('citados_profiles_public').select('*'),
        supabase.from('hall_of_fame_snapshots').select('*').order('rank_position', { ascending: true })
      ]);
      
      if (publicRes.error) throw publicRes.error;
      if (snapshotsRes.error) console.error('Error fetching hall of fame snapshots:', snapshotsRes.error);
      
      if (snapshotsRes.data) {
        setHallOfFameSnapshots(snapshotsRes.data);
      }
      
      let allProfiles = publicRes.data || [];

      if (currentUser) {
        const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;
        const { data: privateData, error: privateError } = await client
          .from('citados_profiles')
          .select('*')
          .eq('user_id', currentUser.id);
        
        if (!privateError && privateData && privateData.length > 0) {
          // Check if user is admin in any of their profiles
          const amIAdmin = privateData.some(p => p.is_admin === true);
          setIsAdmin(amIAdmin);
          
          privateData.forEach(myProfile => {
            const index = allProfiles.findIndex(p => p.id === myProfile.id);
            if (index !== -1) {
              allProfiles[index] = { ...allProfiles[index], ...myProfile };
            } else {
              allProfiles.push(myProfile);
            }
          });
        }
      }
      
      const mappedProfiles = allProfiles.map(mapProfile);
      
      setProfiles(() => {
        if (!currentUser) {
          // GUEST: Show real + mock profiles
          let mappedMockData = [];
          try {
            mappedMockData = MOCK_DATA.map(p => {
              try {
                return mapProfile(p);
              } catch (e) {
                console.error("Error mapping individual mock profile:", p.id, e);
                return null;
              }
            }).filter(Boolean);
          } catch (e) {
            console.error("Error mapping MOCK_DATA array:", e);
          }
          
          const combined = [...mappedProfiles, ...mappedMockData];
          return combined.sort((a, b) => (b.xp || 0) - (a.xp || 0));
        }
        return mappedProfiles;
      });
    } catch (err) {
      console.error('Error fetching profiles:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileComplete = async (profileData) => {
    if (!user) {
      setShowRegister(true);
      return;
    }

    const existingSameRole = profiles.find(p => p.userId === user.id && p.type === profileData.category);

    if (existingSameRole && !editingProfile) {
      setNotification({ 
        type: 'error', 
        message: `Ya tenés un perfil de ${profileData.category === 'jugador' ? 'Jugador' : 'Equipo'}. Editá el existente.` 
      });
      return;
    }

    setIsSaving(true);
    const profileRow = {
      user_id: user.id,
      name: profileData.name,
      show_full_name: profileData.showFullName,
      type: profileData.category,
      city: profileData.city,
      area: profileData.area,
      level: profileData.level,
      age_range: profileData.ageRange,
      gender: profileData.gender,
      is_available: profileData.isAvailable,
      available_from: profileData.availableFrom,
      available_to: profileData.availableTo,
      is_mixed_available: profileData.isMixedAvailable,
      category: profileData.category === 'equipo' ? profileData.teamCategory : null,
      positions: profileData.positions,
      availability: profileData.availability,
      image_url: profileData.image
    };

    if (editingProfile?.id) {
      profileRow.id = editingProfile.id;
    }

    try {
      const { error } = await supabase
        .from('citados_profiles')
        .upsert([profileRow], { onConflict: 'user_id,type' });
      
      if (error) {
        console.error('Error saving to citados_profiles:', error);
        setNotification({ 
          type: 'error', 
          message: error.code === '23505' ? 'Ya tenés un perfil con este rol registrado.' : 'Error al guardar el perfil. Reintentá en unos segundos.' 
        });
        setIsSaving(false);
      } else {
        setView('landing');
        setEditingProfile(null);
        setIsSaving(false);
        setNotification({ type: 'success', message: '¡Perfil guardado con éxito!' });
        fetchProfiles();
      }
    } catch (err) {
      console.error('Error in handleProfileComplete:', err);
      setIsSaving(false);
      setNotification({ type: 'error', message: 'Ocurrió un error inesperado al guardar el perfil.' });
    }
  };

  const handleToggleProfileField = async (profileId, fieldName, newValue) => {
    if (!user || !profileId) return;

    // Map frontend camelCase fields to database snake_case columns
    const dbFieldMap = {
      isAvailable: 'is_available',
      isMixedAvailable: 'is_mixed_available',
      showFullName: 'show_full_name'
    };
    const dbColumnName = dbFieldMap[fieldName] || fieldName;

    // Optimistic UI Update
    setProfiles(prev => prev.map(p => 
      p.id === profileId ? { 
        ...p, 
        [fieldName]: newValue,
        // If we are changing the name toggle, we should also re-format the name in the profiles list
        name: fieldName === 'showFullName' 
          ? formatPlayerName(p.fullName || p.name, newValue, false, p.type) 
          : p.name
      } : p
    ));

    try {
      const { error } = await supabase
        .from('citados_profiles')
        .update({ [dbColumnName]: newValue })
        .eq('id', profileId)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }
      
      const messages = {
        isAvailable: newValue ? 'Ahora estás visible en las búsquedas.' : 'Ahora estás oculto en las búsquedas.',
        isMixedAvailable: newValue ? 'Habilitaste los partidos mixtos.' : 'Deshabilitaste los partidos mixtos.',
        showFullName: newValue ? 'Tu apellido completo ahora es público.' : 'Tu apellido completo ahora está oculto.'
      };
      
      setNotification({ 
        type: 'success', 
        message: messages[fieldName] || 'Preferencia actualizada correctamente.' 
      });
      fetchProfiles();
    } catch (err) {
      console.error(`Error in handleToggleProfileField for ${fieldName}:`, err);
      setNotification({ type: 'error', message: 'Error al actualizar la preferencia.' });
      
      // Revert optimistic update on error
      setProfiles(prev => prev.map(p => 
        p.id === profileId ? { 
          ...p, 
          [fieldName]: !newValue,
          name: fieldName === 'showFullName' 
            ? formatPlayerName(p.fullName || p.name, !newValue, true, p.type) 
            : p.name
        } : p
      ));
    }
  };

  const handleToggleAvailability = async (profileId, newAvailability) => {
    await handleToggleProfileField(profileId, 'isAvailable', newAvailability);
  };

  const handleDeleteProfile = async (profileId) => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      // Use .select() to verify the delete actually removed rows (RLS may silently block it)
      const { data: deletedRows, error } = await supabase
        .from('citados_profiles')
        .delete()
        .eq('id', profileId)
        .eq('user_id', user.id)
        .select();
        
      if (error) throw error;

      // If no rows were returned, the delete was silently blocked (e.g. missing RLS DELETE policy)
      if (!deletedRows || deletedRows.length === 0) {
        throw new Error('No se pudo eliminar el perfil. Verificá los permisos o contactá soporte.');
      }
      
      // Determine if the deleted profile was a team profile for cleanup
      const deletedProfile = profiles.find(p => p.id === profileId);
      const wasTeamProfile = deletedProfile?.type === 'equipo';

      // Optimistic state update: immediately remove the profile from local state
      setProfiles(prev => prev.filter(p => p.id !== profileId));

      // Clean up related team state if we deleted a team profile
      if (wasTeamProfile) {
        setPendingTeamMatch(null);
      }
      
      setNotification({ type: 'success', message: '¡Perfil eliminado con éxito!' });
      
      // Re-fetch from DB as backup to ensure sync
      fetchProfiles();
      
      // Validar si el usuario ya no tiene ningún perfil, redirigirlo
      const remainingUserProfiles = profiles.filter(p => p.userId === user.id && p.id !== profileId);
      if (remainingUserProfiles.length === 0) {
        setView('landing');
      }
    } catch (err) {
      console.error('Error in handleDeleteProfile:', err);
      setNotification({ type: 'error', message: err.message || 'Error al eliminar el perfil.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateSOSAlert = async (pitchName, scheduledAt, matchType, positionNeeded, hostProfileId) => {
    if (!user) return { success: false, error: 'Usuario no autenticado' };
    
    setIsSaving(true);
    try {
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;
      
      // 1. Consume 1 credit
      const { data: creditConsumed, error: creditErr } = await client.rpc('consume_credit', { p_profile_id: hostProfileId });
      if (creditErr) throw creditErr;
      
      if (!creditConsumed) {
        setNotification({
          type: 'error',
          message: 'No tenés créditos disponibles para activar el Botón de Pánico. Por favor comprá créditos.'
        });
        return { success: false, error: 'Sin créditos' };
      }

      // 2. Insert SOS Booking
      const { data: booking, error: insertErr } = await client
        .from('citados_player_bookings')
        .insert({
          host_id: user.id,
          host_profile_id: hostProfileId,
          player_id: null,
          status: 'pending_sos',
          fee_paid: 2000,
          scheduled_at: scheduledAt,
          pitch_name: pitchName,
          match_type_label: matchType,
          used_credit: true,
          is_sos: true,
          sos_position: positionNeeded
        })
        .select()
        .single();

      if (insertErr) {
        // Rollback credit if booking fails
        await client.rpc('add_credit', { p_profile_id: hostProfileId });
        throw insertErr;
      }

      setNotification({
        type: 'success',
        message: '🚨 ¡Alerta de Pánico Activada! Se notificará a los jugadores de la zona.'
      });

      // Insert notification for administrative tracking/internal logging
      try {
        await client.from('citados_notifications').insert({
          user_id: user.id,
          title: '🚨 SOS Activado',
          body: `Activaste una alerta de pánico para tu partido en ${pitchName}. Esperando refuerzo.`,
          type: 'info',
          url: '/profile'
        });
      } catch (e) {}

      fetchProfiles();
      fetchInvitations();
      return { success: true, booking };
    } catch (err) {
      console.error('Error in handleCreateSOSAlert:', err);
      setNotification({ type: 'error', message: 'Error al activar el botón de pánico: ' + err.message });
      return { success: false, error: err.message };
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSOSAlert = async (bookingId, hostProfileId) => {
    if (!user) return { success: false, error: 'Usuario no autenticado' };
    
    setIsSaving(true);
    try {
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;

      // 1. Verify and update booking status to cancelled
      const { data, error: updateErr } = await client
        .from('citados_player_bookings')
        .update({ status: 'cancelled' })
        .eq('id', bookingId)
        .eq('host_id', user.id)
        .eq('status', 'pending_sos')
        .select()
        .single();

      if (updateErr) throw updateErr;

      // 2. Refund 1 credit to host
      const { error: refundErr } = await client.rpc('add_credit', { p_profile_id: hostProfileId });
      if (refundErr) throw refundErr;

      setNotification({
        type: 'success',
        message: '🚨 Alerta SOS cancelada. Se te reintegró 1 crédito a tu cuenta.'
      });

      fetchProfiles();
      fetchInvitations();
      return { success: true };
    } catch (err) {
      console.error('Error in handleCancelSOSAlert:', err);
      setNotification({ type: 'error', message: 'Error al cancelar alerta de pánico: ' + err.message });
      return { success: false, error: err.message };
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptSOSAlert = async (bookingId, guestProfileId) => {
    if (!user) return { success: false, error: 'Usuario no autenticado' };

    setIsConfirmingMatch(true);
    try {
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;

      // 1. Atomically claim the SOS booking
      const { data, error: updateErr } = await client
        .from('citados_player_bookings')
        .update({
          player_id: guestProfileId,
          status: 'accepted'
        })
        .eq('id', bookingId)
        .eq('status', 'pending_sos')
        .is('player_id', null)
        .select()
        .maybeSingle();

      if (updateErr) throw updateErr;

      if (!data) {
        setNotification({
          type: 'error',
          message: 'Lo sentimos, este puesto ya fue tomado por otro jugador. ¡Más rápido la próxima! ⚡'
        });
        fetchInvitations();
        return { success: false, error: 'Lugar ya ocupado' };
      }

      setNotification({
        type: 'success',
        message: '¡Aceptaste la alerta de pánico! Chat habilitado para coordinar.'
      });

      // 2. Notify the host in real-time
      try {
        const guestProfile = profiles.find(p => p.id === guestProfileId);
        await client.from('citados_notifications').insert({
          user_id: data.host_id,
          title: '🚨 ¡SOS Respondido! ⚽',
          body: `${guestProfile?.name || 'Un jugador'} aceptó tu alerta de pánico para el partido en ${data.pitch_name}.`,
          type: 'success',
          url: '/profile'
        });
      } catch (err) {
        console.error('Error al notificar al host del SOS:', err);
      }

      fetchProfiles();
      fetchInvitations();
      return { success: true, booking: data };
    } catch (err) {
      console.error('Error in handleAcceptSOSAlert:', err);
      setNotification({ type: 'error', message: 'Error al aceptar alerta de pánico: ' + err.message });
      return { success: false, error: err.message };
    } finally {
      setIsConfirmingMatch(false);
    }
  };

  const fetchAdminStats = async () => {
    if (!isAdmin) return;
    
    try {
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;
      
      // 1. Total Bookings count
      const { count: totalBookings } = await client
        .from('citados_player_bookings')
        .select('*', { count: 'exact', head: true });

      // 2. Confirmed Bookings count
      const { count: confirmedBookings } = await client
        .from('citados_player_bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'agendado');

      // 3. Pending Bookings (active FOMO)
      const { count: pendingBookings } = await client
        .from('citados_player_bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pendiente');

      // 4. Total Profiles
      const { count: totalUsers } = await client
        .from('citados_profiles')
        .select('*', { count: 'exact', head: true });

      // 5. Recent Activity (last 10 bookings)
      const { data: recentActivity } = await client
        .from('citados_player_bookings')
        .select(`
          *,
          host:host_profile_id(name, image_url),
          player:player_id(name, image_url)
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      setAdminStats({
        totalBookings: totalBookings || 0,
        confirmedBookings: confirmedBookings || 0,
        pendingBookings: pendingBookings || 0,
        totalUsers: totalUsers || 0,
        successRate: totalBookings > 0 ? Math.round((confirmedBookings / totalBookings) * 100) : 0,
        recentActivity: recentActivity || [],
        disputesCount: 0, // Will be updated by fetchDisputes
        totalRevenue: 0 // Placeholder for payments
      });

      fetchDisputes();
    } catch (err) {
      console.error('Error fetching admin stats:', err);
    }
  };

  const fetchDisputes = async () => {
    if (!isAdmin) return;
    try {
      const client = (import.meta.env.DEV && supabaseAdmin) ? supabaseAdmin : supabase;
      
      // 1. Team Match Disputes
      const { data: teamDisputes } = await client
        .from('citados_team_matches')
        .select(`
          *,
          reporter:reporter_team_id(name, image_url),
          opponent:opponent_team_id(name, image_url)
        `)
        .eq('status', 'disputado');

      // 2. No-Show Disputes
      const { data: noShowDisputes } = await client
        .from('citados_player_bookings')
        .select(`
          *,
          host:host_profile_id(name, image_url),
          player:player_id(name, image_url)
        `)
        .eq('no_show_status', 'disputed');

      const allDisputes = [
        ...(teamDisputes || []).map(d => ({ ...d, type: 'team_match' })),
        ...(noShowDisputes || []).map(d => ({ ...d, type: 'no_show' }))
      ];

      setDisputes(allDisputes);
      setAdminStats(prev => ({ ...prev, disputesCount: allDisputes.length }));
    } catch (err) {
      console.error('Error fetching disputes:', err);
    }
  };

  const handleDevLogin = (mockId = '00000000-0000-0000-0000-000000000000', name = 'Admin Dev', email = 'admin@citados.com.ar') => {
    if (import.meta.env.DEV) {
      const mockUser = {
        id: mockId,
        email: email,
        user_metadata: { full_name: name }
      };
      setUser(mockUser);
      setShowRegister(false);
      if (email === 'admin@citados.com.ar') setIsAdmin(true);
      
      setReceivedPending([]);
      setSentPending([]);
      setConfirmedInvitations([]);
      fetchProfiles(mockUser);
    }
  };

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('citados_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      setInternalNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.is_read).length || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        .from('citados_notifications')
        .update({ is_read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      setInternalNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllNotificationsAsRead = async () => {
    if (!user || unreadCount === 0) return;
    try {
      const { error } = await supabase
        .from('citados_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      
      if (error) throw error;
      setInternalNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleResolveDispute = async (disputeId, type, resolution) => {
    try {
      if (type === 'team_match') {
        const { error } = await supabase
          .from('citados_team_matches')
          .update({ status: resolution === 'approve' ? 'confirmado' : 'rechazado' })
          .eq('id', disputeId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('citados_no_shows')
          .update({ status: resolution === 'approve' ? 'confirmado' : 'rechazado' })
          .eq('id', disputeId);
        if (error) throw error;
      }
      
      setNotification({
        type: 'success',
        message: `Disputa ${resolution === 'approve' ? 'aprobada' : 'rechazada'} correctamente.`
      });
      
      // Refrescar disputas
      fetchDisputes();
    } catch (err) {
      console.error('Error resolving dispute:', err.message);
      setNotification({
        type: 'error',
        message: 'No se pudo resolver la disputa.'
      });
    }
  };

  return {
    user,
    profiles,
    hallOfFameSnapshots,
    isLoading,
    isAdmin,
    adminStats,
    fetchAdminStats,
    receivedPending,
    sentPending,
    confirmedInvitations,
    pendingNoShowReports,
    recentMatches,
    pendingTeamMatch,
    setPendingTeamMatch,
    activeMatchToRate,
    setActiveMatchToRate,
    showRatingModal,
    setShowRatingModal,
    isConfirmingMatch,
    isSaving,
    setIsSaving,
    editingProfile,
    setEditingProfile,
    fetchProfiles,
    searchByRadius,
    fetchInvitations,
    handleAcceptInvitation,
    handleRejectInvitation,
    handleConfirmTeamMatch,
    handleDisputeTeamMatch,
    handleDisputeNoShow,
    handleConfirmNoShow,
    handleSaveRating,
    handleProfileComplete,
    handleToggleAvailability,
    handleToggleProfileField,
    handleDeleteProfile,
    handleDevLogin,
    internalNotifications,
    unreadCount,
    fetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    disputes,
    fetchDisputes,
    handleResolveDispute,
    openSOSAlerts,
    handleCreateSOSAlert,
    handleCancelSOSAlert,
    handleAcceptSOSAlert
  };
};
